import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
    InvoiceCreationProcessFailure,
    InvoiceCreationProcessSuccess,
    StartInvoiceCreationProcess
} from '../actions/invoice-creation-process.actions';
import { catchError, mergeMap, Observable, of, switchMap, withLatestFrom } from 'rxjs';
import { Store } from '@ngrx/store';
import { invoiceCreationProcessData } from '../reducer/invoice-creation-process.reducer';
import { InvoiceControllerService, InvoiceCreationSummary } from '@iota/iota-management-portal-v4-client';
import {
    BusinessPartnerType,
    InvoiceCreationProcessData,
    InvoicingType
} from 'src/app/shared/constants-and-enums/invoice-creation-process-types';
import { LoadAllAuditInvoiceSummaries } from '../actions/audit-invoice-summary.actions';
import { auditInvoiceSummarySelectedDateFrom, auditInvoiceSummarySelectedDateTo } from '../reducer/audit-invoice-summary.reducer';

@Injectable()
export class InvoiceCreationProcessEffects {
    startInvoiceCreationProcess$ = createEffect(() =>
        this.actions$.pipe(
            ofType(StartInvoiceCreationProcess),
            withLatestFrom(
                this.store.select(invoiceCreationProcessData),
                this.store.select(auditInvoiceSummarySelectedDateFrom),
                this.store.select(auditInvoiceSummarySelectedDateTo)
            ),
            mergeMap(([, data, from, to]) =>
                this.startInvoiceCreationProcess(data).pipe(
                    switchMap((result) => of(InvoiceCreationProcessSuccess({ result }), LoadAllAuditInvoiceSummaries({ from, to }))),
                    catchError((error) => of(InvoiceCreationProcessFailure({ error })))
                )
            )
        )
    );

    constructor(
        private actions$: Actions,
        private apiService: InvoiceControllerService,
        private store: Store
    ) {}

    private startInvoiceCreationProcess(data: InvoiceCreationProcessData): Observable<InvoiceCreationSummary> {
        if (data.businessPartnerType === BusinessPartnerType.CENTRAL_REGULATORS && data.invoicingType === InvoicingType.PRODUCT_INVOICE) {
            return this.apiService.startCentralRegulatorProductInvoiceProcess(data.businessPartnerIds, data.locale);
        } else if (
            data.businessPartnerType === BusinessPartnerType.CENTRAL_REGULATORS &&
            data.invoicingType === InvoicingType.PLATFORM_LICENSE_INVOICE
        ) {
            return this.apiService.startCentralRegulatorPlatformLicenseInvoiceProcess(data.businessPartnerIds, data.locale);
        } else if (data.businessPartnerType === BusinessPartnerType.CUSTOMERS && data.invoicingType === InvoicingType.PRODUCT_INVOICE) {
            return this.apiService.startCompanyProductInvoiceProcess(data.businessPartnerIds, data.locale);
        } else if (
            data.businessPartnerType === BusinessPartnerType.CUSTOMERS &&
            data.invoicingType === InvoicingType.PLATFORM_LICENSE_INVOICE
        ) {
            return this.apiService.startCompanyPlatformLicenseInvoiceProcess(data.businessPartnerIds, data.locale);
        }

        return of(null);
    }
}
